import { motion } from 'motion/react';
import { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { SuccessMetricsBar } from '../components/process/SuccessMetricsBar';
import { ProcessHeroProfessional } from '../components/process/ProcessHeroProfessional';
import { ProcessStepsProvider, useProcessSteps } from '../contexts/ProcessStepsContext';
import { Helmet } from 'react-helmet-async';
import { getApiUrl } from '../utils/apiConfig';

// Lazy load heavy components for better performance
const OrbitSystem = lazy(() => import('../components/process/orbit/OrbitSystem').then(m => ({ default: m.OrbitSystem })));
const TimelineView = lazy(() => import('../components/process/TimelineView').then(m => ({ default: m.TimelineView })));
const WhySixSteps = lazy(() => import('../components/process/WhySixSteps').then(m => ({ default: m.WhySixSteps })));
const RiskFreeGuarantee = lazy(() => import('../components/process/RiskFreeGuarantee').then(m => ({ default: m.RiskFreeGuarantee })));
const ProcessFinalCTA = lazy(() => import('../components/process/ProcessFinalCTA').then(m => ({ default: m.ProcessFinalCTA })));
const TestimonialsSection = lazy(() => import('../components/sections/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const ProcessPageFAQ = lazy(() => import('../components/process/ProcessPageFAQ').then(m => ({ default: m.ProcessPageFAQ })));

// Skeleton loading components
function SkeletonBox({ className = '' }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-lg bg-white/10 ${className}`}>
      <div className="absolute inset-0 shimmer" />
    </div>
  );
}

function ViewSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
      <div className="flex flex-col items-center space-y-6">
        <SkeletonBox className="w-48 sm:w-64 h-8" />
        <SkeletonBox className="w-full max-w-2xl h-64 sm:h-96" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          <SkeletonBox className="h-32" />
          <SkeletonBox className="h-32" />
          <SkeletonBox className="h-32" />
        </div>
      </div>
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <SkeletonBox className="w-64 h-10 mx-auto mb-8" />
      <SkeletonBox className="w-full h-48 sm:h-64" />
    </div>
  );
}

// ============================================
// OUTER COMPONENT — just wraps with provider
// ============================================

export function ProcessPage() {
  return (
    <ProcessStepsProvider>
      <ProcessPageContent />
    </ProcessStepsProvider>
  );
}

// ============================================
// INNER COMPONENT — all state and rendering
// ============================================

function ProcessPageContent() {
  const [viewMode, setViewMode] = useState<'orbit' | 'timeline'>('orbit');
  const { data: stepsData, loading } = useProcessSteps();

  // SEO / page settings from CMS
  const [pageSettings, setPageSettings] = useState<any>(null);

  useEffect(() => {
    fetch(`${getApiUrl()}/admin/process-page/settings?t=${Date.now()}`)
      .then(r => r.json())
      .then(r => { if (r.success && r.data) setPageSettings(r.data); })
      .catch(() => {});
  }, []);

  // Pre-calculate star positions for consistent rendering (REDUCED FROM 200 TO 40)
  const starPositions = useMemo(() => {
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 0.5,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 2 + Math.random() * 3,
      delay: Math.random() * 2,
    }));
  }, []);

  // Pre-calculate bright star positions (REDUCED FROM 20 TO 8)
  const brightStarPositions = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => ({
      id: i,
      size: 3 + Math.random() * 2,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 1.5 + Math.random() * 2,
      delay: Math.random() * 2,
    }));
  }, []);

  // Pre-calculate particle positions (REDUCED FROM 30 TO 10)
  const particlePositions = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      moveX: Math.random() * 40 - 20,
      moveY: Math.random() * 40 - 20,
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 3,
    }));
  }, []);

  return (
    <div className="relative overflow-hidden">

      {/* SEO — CMS first, fallback second */}
      <Helmet>
        <title>{pageSettings?.seo?.metaTitle || 'Our Process | Creabiz Studio'}</title>
        <meta name="description" content={pageSettings?.seo?.metaDescription || 'Discover our proven 6-step animation process. From concept to delivery, we create professional 3D animations that drive real business results.'} />
        <meta property="og:title" content={pageSettings?.seo?.ogTitle || pageSettings?.seo?.metaTitle || 'Our Process | Creabiz Studio'} />
        <meta property="og:description" content={pageSettings?.seo?.ogDescription || pageSettings?.seo?.metaDescription || 'Our proven 6-step animation process from concept to delivery.'} />
        {pageSettings?.seo?.ogImage && <meta property="og:image" content={pageSettings.seo.ogImage} />}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageSettings?.seo?.ogTitle || pageSettings?.seo?.metaTitle || 'Our Process | Creabiz Studio'} />
        <meta property="twitter:description" content={pageSettings?.seo?.ogDescription || pageSettings?.seo?.metaDescription || 'Our proven 6-step animation process from concept to delivery.'} />
      </Helmet>

      {/* OPTIMIZED UNIVERSE THEME - SEAMLESS BACKGROUND */}
      <div className="relative bg-gradient-to-br from-[#0d0221] via-[#1f0e3d] to-[#12062b]">
        {/* OPTIMIZED DEEP SPACE BACKGROUND
            FIX: changed from fixed to absolute — fixed forces a repaint on every
            scroll event because the browser must recomposite the entire page. */}
        <div className="absolute inset-0 pointer-events-none z-0">
          {/* Rich Gradient Layers for Depth */}
          <div className="absolute inset-0 bg-gradient-to-tr from-[#2a0d4e]/70 via-transparent to-[#0d2847]/50" />
          <div className="absolute inset-0 bg-gradient-to-bl from-[#3d1f5e]/60 via-transparent to-[#1f0e3d]/70" />

          {/* SOFT ETHEREAL Nebula Clouds - Ultra smooth feathered edges */}
          {/* Orange/Gold Nebula - Top Right */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] sm:w-[1200px] sm:h-[1200px] opacity-50">
            <div className="absolute inset-0 rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle at center, rgba(232, 125, 62, 0.15), rgba(232, 184, 77, 0.10), rgba(232, 184, 77, 0.05), transparent 70%)', willChange: 'transform' }} />
            <div className="absolute inset-[15%] rounded-full blur-[80px]" style={{ background: 'radial-gradient(circle at center, rgba(232, 125, 62, 0.18), rgba(232, 184, 77, 0.12), transparent 60%)', willChange: 'transform' }} />
            <div className="absolute inset-[30%] rounded-full blur-[50px]" style={{ background: 'radial-gradient(circle at center, rgba(232, 125, 62, 0.22), rgba(232, 184, 77, 0.15), transparent 50%)', willChange: 'transform' }} />
          </div>

          {/* Purple/Pink Nebula - Top Left */}
          <div className="absolute top-1/4 left-0 w-[700px] h-[700px] sm:w-[1000px] sm:h-[1000px] opacity-50">
            <div className="absolute inset-0 rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle at center, rgba(168, 85, 247, 0.14), rgba(236, 72, 153, 0.10), rgba(236, 72, 153, 0.05), transparent 70%)', willChange: 'transform' }} />
            <div className="absolute inset-[15%] rounded-full blur-[80px]" style={{ background: 'radial-gradient(circle at center, rgba(168, 85, 247, 0.17), rgba(236, 72, 153, 0.12), transparent 60%)', willChange: 'transform' }} />
            <div className="absolute inset-[30%] rounded-full blur-[50px]" style={{ background: 'radial-gradient(circle at center, rgba(168, 85, 247, 0.20), rgba(236, 72, 153, 0.15), transparent 50%)', willChange: 'transform' }} />
          </div>

          {/* Green/Blue Nebula - Bottom Right (Desktop only) */}
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] sm:w-[900px] sm:h-[900px] hidden sm:block opacity-45">
            <div className="absolute inset-0 rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.12), rgba(74, 144, 226, 0.08), rgba(74, 144, 226, 0.04), transparent 70%)', willChange: 'transform' }} />
            <div className="absolute inset-[15%] rounded-full blur-[70px]" style={{ background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.15), rgba(74, 144, 226, 0.10), transparent 60%)', willChange: 'transform' }} />
            <div className="absolute inset-[30%] rounded-full blur-[45px]" style={{ background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.18), rgba(74, 144, 226, 0.12), transparent 50%)', willChange: 'transform' }} />
          </div>

          {/* Orange/Red Nebula - Center (Desktop only) */}
          <div className="absolute top-1/2 left-1/3 w-[550px] h-[550px] sm:w-[850px] sm:h-[850px] hidden sm:block opacity-40">
            <div className="absolute inset-0 rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.10), rgba(239, 68, 68, 0.07), rgba(239, 68, 68, 0.04), transparent 70%)', willChange: 'transform' }} />
            <div className="absolute inset-[15%] rounded-full blur-[70px]" style={{ background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.13), rgba(239, 68, 68, 0.09), transparent 60%)', willChange: 'transform' }} />
            <div className="absolute inset-[30%] rounded-full blur-[45px]" style={{ background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.16), rgba(239, 68, 68, 0.11), transparent 50%)', willChange: 'transform' }} />
          </div>

          {/* OPTIMIZED Star Field - CSS ANIMATION - REDUCED FROM 200 TO 40 */}
          <div className="absolute inset-0 opacity-90">
            {starPositions.map((star) => (
              <div
                key={`star-${star.id}`}
                className="absolute bg-white rounded-full star-twinkle"
                style={{
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  left: `${star.left}%`,
                  top: `${star.top}%`,
                  animationDuration: `${star.duration}s`,
                  animationDelay: `${star.delay}s`,
                  willChange: 'opacity, transform',
                }}
              />
            ))}
          </div>

          {/* OPTIMIZED Shooting Stars - REDUCED FROM 7 TO 3 */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={`shooting-star-${i}`}
              className="absolute h-[1px] bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
              style={{
                width: '150px',
                top: `${20 + i * 30}%`,
                left: '-150px',
                rotate: '-45deg',
                willChange: 'transform, opacity',
              }}
              animate={{ x: ['0px', '100vw'], opacity: [0, 1, 0.8, 0] }}
              transition={{ duration: 1.5, delay: 4 + i * 6, repeat: Infinity, repeatDelay: 15, ease: 'easeIn' }}
            />
          ))}

          {/* OPTIMIZED Meteoroids - REDUCED FROM 4 TO 2, REMOVED BOX-SHADOW */}
          {[0, 1].map((i) => (
            <motion.div key={`meteor-${i}`} className="absolute hidden sm:block" style={{ left: `${30 + i * 40}%`, top: '-50px' }}>
              <motion.div
                className="relative w-2 h-2 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-full"
                style={{ willChange: 'transform, opacity' }}
                animate={{ y: ['0vh', '120vh'], x: ['0px', '40px'], opacity: [0, 1, 0.8, 0] }}
                transition={{ duration: 3, delay: 6 + i * 10, repeat: Infinity, repeatDelay: 25, ease: 'easeIn' }}
              />
              <motion.div
                className="absolute top-0 left-0 w-[2px] h-20 bg-gradient-to-b from-[#E87D3E]/60 via-[#E8B84D]/30 to-transparent"
                style={{ willChange: 'transform, opacity' }}
                animate={{ y: ['0vh', '120vh'], x: ['0px', '40px'], opacity: [0, 0.8, 0.6, 0] }}
                transition={{ duration: 3, delay: 6 + i * 10, repeat: Infinity, repeatDelay: 25, ease: 'easeIn' }}
              />
            </motion.div>
          ))}

          {/* OPTIMIZED Comets - REDUCED FROM 3 TO 2, REMOVED BOX-SHADOW */}
          {[0, 1].map((i) => (
            <motion.div key={`comet-${i}`} className="absolute hidden lg:block" style={{ left: `${20 + i * 50}%`, top: '-100px' }}>
              <motion.div
                className="relative w-3 h-3 bg-gradient-to-br from-[#4facfe] to-[#00f2fe] rounded-full"
                style={{ willChange: 'transform, opacity' }}
                animate={{ y: ['0vh', '130vh'], x: ['0px', '60px'], opacity: [0, 1, 0.9, 0] }}
                transition={{ duration: 4, delay: 10 + i * 15, repeat: Infinity, repeatDelay: 35, ease: 'easeIn' }}
              />
              <motion.div
                className="absolute top-0 left-0 w-[3px] h-32 bg-gradient-to-b from-[#4facfe]/80 via-[#00f2fe]/40 to-transparent"
                style={{ willChange: 'transform, opacity' }}
                animate={{ y: ['0vh', '130vh'], x: ['0px', '60px'], opacity: [0, 0.9, 0.7, 0] }}
                transition={{ duration: 4, delay: 10 + i * 15, repeat: Infinity, repeatDelay: 35, ease: 'easeIn' }}
              />
            </motion.div>
          ))}

          {/* OPTIMIZED Glowing Particles - CSS ANIMATION - REDUCED FROM 30 TO 10 */}
          {particlePositions.map((particle) => (
            <div
              key={`firefly-${particle.id}`}
              className="absolute w-[2px] h-[2px] bg-white rounded-full particle-float hidden sm:block"
              style={{
                left: `${particle.left}%`,
                top: `${particle.top}%`,
                animationDuration: `${particle.duration}s`,
                animationDelay: `${particle.delay}s`,
                willChange: 'transform, opacity',
              }}
            />
          ))}

          {/* OPTIMIZED Twinkling Bright Stars - CSS ANIMATION - REDUCED FROM 20 TO 8 */}
          {brightStarPositions.map((star) => (
            <div
              key={`bright-star-${star.id}`}
              className="absolute bg-white rounded-full bright-star-twinkle"
              style={{
                width: `${star.size}px`,
                height: `${star.size}px`,
                left: `${star.left}%`,
                top: `${star.top}%`,
                animationDuration: `${star.duration}s`,
                animationDelay: `${star.delay}s`,
                willChange: 'opacity, transform',
              }}
            />
          ))}
        </div>

        {/* CONTENT - ALL SECTIONS */}
        <div className="relative z-10">
          {/* HERO — eagerly imported, no Suspense needed */}
          <ProcessHeroProfessional />

          {/* Success Metrics Bar — eagerly imported, no Suspense needed */}
          <SuccessMetricsBar />

          {/* View Toggle */}
          {!loading && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex justify-center"
              >
                <div className="inline-flex items-center bg-white/10 backdrop-blur-2xl rounded-full p-1 sm:p-1.5 border border-white/20 shadow-2xl w-full sm:w-auto">
                  <button
                    onClick={() => setViewMode('orbit')}
                    className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition-all duration-300 flex-1 sm:flex-initial ${
                      viewMode === 'orbit'
                        ? 'bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] text-white shadow-lg'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    <span className="text-sm sm:text-base">{stepsData.viewTabs.orbit.icon}</span>
                    <span className="text-xs sm:text-sm">{stepsData.viewTabs.orbit.text}</span>
                  </button>
                  <button
                    onClick={() => setViewMode('timeline')}
                    className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition-all duration-300 flex-1 sm:flex-initial ${
                      viewMode === 'timeline'
                        ? 'bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] text-white shadow-lg'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    <span className="text-sm sm:text-base">{stepsData.viewTabs.timeline.icon}</span>
                    <span className="text-xs sm:text-sm">{stepsData.viewTabs.timeline.text}</span>
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Orbit or Timeline View — lazy, show shimmer while loading */}
          <Suspense fallback={<ViewSkeleton />}>
            {viewMode === 'orbit' ? <OrbitSystem /> : <TimelineView />}
          </Suspense>

          {/* Why 6 Steps — lazy */}
          <Suspense fallback={<SectionSkeleton />}>
            <WhySixSteps />
          </Suspense>

          {/* Risk-Free Guarantee — lazy */}
          <Suspense fallback={<SectionSkeleton />}>
            <RiskFreeGuarantee />
          </Suspense>

          {/* Final CTA — lazy */}
          <Suspense fallback={<SectionSkeleton />}>
            <ProcessFinalCTA />
          </Suspense>
        </div>
      </div>

      {/* CLIENT TESTIMONIALS — lazy */}
      <Suspense fallback={<SectionSkeleton />}>
        <TestimonialsSection />
      </Suspense>

      {/* FAQ SECTION — lazy */}
      <Suspense fallback={<SectionSkeleton />}>
        <ProcessPageFAQ />
      </Suspense>

      {/* OPTIMIZED CSS ANIMATIONS */}
      <style>{`
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        .star-twinkle {
          animation: starTwinkle infinite ease-in-out;
        }

        @keyframes brightStarTwinkle {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        .bright-star-twinkle {
          animation: brightStarTwinkle infinite ease-in-out;
          filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.8));
        }

        @keyframes particleFloat {
          0%, 100% { opacity: 0.3; transform: translate(0, 0) scale(0.8); }
          25% { transform: translate(10px, -10px) scale(1.2); }
          50% { opacity: 1; transform: translate(0, -20px) scale(1.5); }
          75% { transform: translate(-10px, -10px) scale(1.2); }
        }
        .particle-float {
          animation: particleFloat infinite ease-in-out;
          filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.6));
        }

        .star-twinkle,
        .bright-star-twinkle,
        .particle-float {
          will-change: opacity, transform;
          backface-visibility: hidden;
          perspective: 1000px;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.6; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}